import React, { useState, useEffect } from 'react';
import { Checkpoint, Terminal } from '../../types';
import { useEnhancedApi } from '../../hooks/useEnhancedApi';
import { useToast } from '../ui/Toast';
import { permissionChecks } from '../../lib/auth/helpers';

export const CheckpointsManagement: React.FC = () => {
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] = useState<Checkpoint | null>(null);
  const [viewingCheckpoint, setViewingCheckpoint] = useState<Checkpoint | null>(null);
  const [loading, setLoading] = useState(false);
  const [terminalsLoading, setTerminalsLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState<{ [key: number]: boolean }>({});
  
  const [formData, setFormData] = useState({
    terminal_id: '',
    checkpoint_name: '',
    description: ''
  });
  
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const { showToast } = useToast();
  
  // Use authenticated API service
  const enhancedApi = useEnhancedApi();

  useEffect(() => {
    loadCheckpoints();
    loadTerminals();
  }, []);

  const loadCheckpoints = async () => {
    try {
      setLoading(true);
      const allCheckpoints = await enhancedApi.getCheckpoints();
      setCheckpoints(allCheckpoints);
    } catch (error) {
      console.error('Failed to load checkpoints:', error);
      showToast('Failed to load checkpoints', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadTerminals = async () => {
    try {
      setTerminalsLoading(true);
      const allTerminals = await enhancedApi.getTerminals();
      // Only show active terminals
      setTerminals(allTerminals.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load terminals:', error);
      showToast('Failed to load terminals', 'error');
    } finally {
      setTerminalsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.terminal_id) {
      errors.terminal_id = 'Please select a terminal';
    }
    
    // Simple validation for checkpoint name
    if (!formData.checkpoint_name.trim()) {
      errors.checkpoint_name = 'Checkpoint name is required';
    } else if (formData.checkpoint_name.length > 100) {
      errors.checkpoint_name = 'Checkpoint name must be 100 characters or less';
    }
    
    if (formData.description && formData.description.length > 500) {
      errors.description = 'Description must be 500 characters or less';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!permissionChecks.canUpdateCheckpoints()) {
      showToast('You do not have permission to create or update checkpoints', 'error');
      return;
    }
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      if (editingCheckpoint) {
        await enhancedApi.updateCheckpoint(editingCheckpoint.checkpoint_id, {
          terminal_id: parseInt(formData.terminal_id),
          checkpoint_name: formData.checkpoint_name,
          description: formData.description.trim() || undefined
        });
        showToast('Checkpoint updated successfully!', 'success');
      } else {
        await enhancedApi.createCheckpoint({
          terminal_id: parseInt(formData.terminal_id),
          checkpoint_name: formData.checkpoint_name,
          description: formData.description.trim() || undefined
        });
        showToast('Checkpoint created successfully!', 'success');
      }
      
      resetForm();
      await loadCheckpoints();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save checkpoint';
      setFormErrors({ submit: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (checkpoint: Checkpoint) => {
    if (!permissionChecks.canUpdateCheckpoints()) {
      showToast('You do not have permission to edit checkpoints', 'error');
      return;
    }
    setEditingCheckpoint(checkpoint);
    setFormData({
      terminal_id: checkpoint.terminal_id.toString(),
      checkpoint_name: checkpoint.checkpoint_name,
      description: checkpoint.description || ''
    });
    setShowAddForm(true);
    setFormErrors({});
  };

  const handleToggleStatus = async (checkpoint: Checkpoint) => {
    if (!permissionChecks.canUpdateCheckpoints()) {
      showToast('You do not have permission to toggle checkpoint status', 'error');
      return;
    }
    if (toggleLoading[checkpoint.checkpoint_id]) return;
    
    setToggleLoading(prev => ({ ...prev, [checkpoint.checkpoint_id]: true }));
    try {
      // Use the correct API endpoint with cascade=true
      await enhancedApi.toggleCheckpoint(
        checkpoint.checkpoint_id, 
        true, // cascade=true
        'admin'
      );
      
      showToast(`Checkpoint ${!checkpoint.is_open ? 'opened' : 'closed'} successfully!`, 'success');
      
      // Hard refresh all cache after toggle (since cascade affects multiple entities)
      await Promise.all([
        loadCheckpoints(), // Refresh checkpoints
        // You might want to refresh other related data here if needed
      ]);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle checkpoint status';
      showToast(errorMessage, 'error');
    } finally {
      setToggleLoading(prev => ({ ...prev, [checkpoint.checkpoint_id]: false }));
    }
  };

  const handleView = (checkpoint: Checkpoint) => {
    setViewingCheckpoint(checkpoint);
  };

  const handleDelete = async (checkpoint: Checkpoint) => {
    if (!permissionChecks.canUpdateCheckpoints()) {
      showToast('You do not have permission to delete checkpoints', 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${checkpoint.checkpoint_name}"?`)) {
      return;
    }
    
    setLoading(true);
    try {
      await enhancedApi.deleteCheckpoint(checkpoint.checkpoint_id);
      showToast('Checkpoint deleted successfully!', 'success');
      await loadCheckpoints();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete checkpoint';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      terminal_id: '',
      checkpoint_name: '',
      description: ''
    });
    setFormErrors({});
    setShowAddForm(false);
    setEditingCheckpoint(null);
  };

  const getTerminalName = (terminalId: number) => {
    const terminal = (Array.isArray(terminals) ? terminals : []).find(t => t.terminal_id === terminalId);
    return terminal ? terminal.terminal_name : `Terminal ${terminalId}`;
  };

  const activeCheckpoints = (Array.isArray(checkpoints) ? checkpoints : []).filter(checkpoint => checkpoint.is_active);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Checkpoints Management</h1>
          <p className="text-gray-400 text-sm">Configure checkpoint settings</p>
        </div>
        {permissionChecks.canUpdateCheckpoints() && (
          <button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors text-sm font-medium"
          >
            {showAddForm ? 'Cancel' : 'Add Checkpoint'}
          </button>
        )}
      </div>
      
      {/* Add/Edit Checkpoint Form */}
      {showAddForm && (
        <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingCheckpoint ? 'Edit Checkpoint' : 'Add New Checkpoint'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Terminal Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Terminal <span className="text-red-400">*</span>
              </label>
              <select 
                name="terminal_id"
                value={formData.terminal_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.terminal_id ? 'border-red-500' : 'border-gray-600'
                }`}
                disabled={loading || terminalsLoading}
              >
                <option value="">Select a terminal</option>
                {terminals.map((terminal) => (
                  <option key={terminal.terminal_id} value={terminal.terminal_id}>
                    {terminal.terminal_name}
                  </option>
                ))}
              </select>
              {formErrors.terminal_id && (
                <p className="mt-1 text-sm text-red-400">{formErrors.terminal_id}</p>
              )}
              {terminalsLoading && (
                <p className="mt-1 text-xs text-gray-400">Loading terminals...</p>
              )}
            </div>

            {/* Checkpoint Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Checkpoint Name <span className="text-red-400">*</span>
              </label>
              <input 
                type="text" 
                name="checkpoint_name"
                value={formData.checkpoint_name}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.checkpoint_name ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter checkpoint name (e.g., Security Checkpoint A)" 
                disabled={loading}
              />
              {formErrors.checkpoint_name && (
                <p className="mt-1 text-sm text-red-400">{formErrors.checkpoint_name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea 
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                  formErrors.description ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter checkpoint description (optional)" 
                disabled={loading}
              />
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-400">{formErrors.description}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">Maximum 500 characters</p>
            </div>

            {/* Submit Error */}
            {formErrors.submit && (
              <div className="p-3 bg-red-900/50 border border-red-500 rounded-md">
                <p className="text-sm text-red-400">{formErrors.submit}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                type="submit" 
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                {loading ? (editingCheckpoint ? 'Updating...' : 'Creating...') : (editingCheckpoint ? 'Update Checkpoint' : 'Create Checkpoint')}
              </button>
              <button 
                type="button" 
                onClick={resetForm}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Checkpoint List */}
      <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4">Checkpoint List</h3>
        {loading && !showAddForm ? (
          <div className="text-center py-8">
            <div className="loading-spinner mx-auto mb-2"></div>
            <p className="text-gray-400">Loading checkpoints...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeCheckpoints.map((checkpoint) => (
              <div key={checkpoint.checkpoint_id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-gray-700 rounded space-y-2 sm:space-y-0">
                <div className="flex-1">
                  <div className="font-medium text-white">{checkpoint.checkpoint_name}</div>
                  <div className="text-sm text-gray-400">
                    Terminal: {getTerminalName(checkpoint.terminal_id)}
                  </div>
                  {checkpoint.description && (
                    <div className="text-sm text-gray-400 mt-1">{checkpoint.description}</div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Updated: {new Date(checkpoint.last_updated).toLocaleDateString()} by {checkpoint.updated_by}
                  </div>
                </div>
                {permissionChecks.canUpdateCheckpoints() ? (
                  <div className="flex flex-wrap gap-2 items-center">
                    {/* Status Toggle */}
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-400">Status</span>
                      <button
                        onClick={() => handleToggleStatus(checkpoint)}
                        disabled={toggleLoading[checkpoint.checkpoint_id] || loading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                          toggleLoading[checkpoint.checkpoint_id] || loading
                            ? 'bg-gray-500 cursor-not-allowed'
                            : checkpoint.is_open 
                            ? 'bg-green-600 focus:ring-green-500' 
                            : 'bg-gray-600 focus:ring-gray-500'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            checkpoint.is_open ? 'translate-x-6' : 'translate-x-1'
                          } ${toggleLoading[checkpoint.checkpoint_id] ? 'animate-pulse' : ''}`}
                        />
                      </button>
                      <span className={`text-xs font-medium ${checkpoint.is_open ? 'text-green-400' : 'text-gray-400'}`}>
                        {toggleLoading[checkpoint.checkpoint_id] ? 'Toggling...' : (checkpoint.is_open ? 'Open' : 'Closed')}
                      </span>
                    </div>
                    
                    {/* Edit Button */}
                    <button 
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                      onClick={() => handleEdit(checkpoint)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-400">Status</span>
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        checkpoint.is_open ? 'bg-green-500' : 'bg-red-500'
                      }`} />
                      <span className="text-sm text-gray-400">
                        {checkpoint.is_open ? 'Open' : 'Closed'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {activeCheckpoints.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-400">
                No checkpoints found. Create your first checkpoint to get started.
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Checkpoint Modal */}
      {viewingCheckpoint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Checkpoint Details</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-400">Name</label>
                <p className="text-white">{viewingCheckpoint.checkpoint_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">Terminal</label>
                <p className="text-white">{getTerminalName(viewingCheckpoint.terminal_id)}</p>
              </div>
              {viewingCheckpoint.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-400">Description</label>
                  <p className="text-white">{viewingCheckpoint.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400">Status</label>
                  <p className={viewingCheckpoint.is_open ? 'text-green-400' : 'text-red-400'}>
                    {viewingCheckpoint.is_open ? 'Open' : 'Closed'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">Active</label>
                  <p className={viewingCheckpoint.is_active ? 'text-blue-400' : 'text-gray-400'}>
                    {viewingCheckpoint.is_active ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">Last Updated</label>
                <p className="text-white">
                  {new Date(viewingCheckpoint.last_updated).toLocaleString()} by {viewingCheckpoint.updated_by}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">Created</label>
                <p className="text-white">{new Date(viewingCheckpoint.created_date).toLocaleString()}</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button 
                onClick={() => setViewingCheckpoint(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
